import { ReactNode } from "react";
import { X } from "lucide-react";
import { ScadenzeRitiro } from "../../api/ritiri";
import { BarraRitiri } from "../../components/ritiri/TestataRitiri";
import { BadgeEsito, giornoBreve } from "./presentazione";
import { BARRE, DATE, ESITI, ETICHETTE, SCENARI } from "./testiLegenda";

/**
 * Legenda della dashboard (sigle, barre, esiti, etichette, scenari). Tutti i
 * testi vengono da `testiLegenda.ts`, gli stessi della scheda "Regole"; le date
 * sono quelle del calendario base che arriva dall'API.
 */

export const ID_TITOLO_LEGENDA = "legenda-ritiri-titolo";

function Sezione({ titolo, children }: { titolo: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-ritiri-bordo bg-white p-4 sm:p-5">
      <h3 className="mb-3 text-base font-bold text-ink">{titolo}</h3>
      {children}
    </section>
  );
}

const ESEMPIO_BARRA = { primary: 0.6, accent: 0.9, caldo: 1 } as const;

export function LegendaRitiri({ base, onChiudi }: { base: ScadenzeRitiro; onChiudi: () => void }) {
  const date: [string, string, string][] = [
    ["D", `${giornoBreve(base.D)} ore 16:00`, DATE.D.testo],
    ["P1", giornoBreve(base.P1), DATE.P1.testo],
    ["P2", giornoBreve(base.P2), DATE.P2.testo],
    [DATE.E.titolo, `${giornoBreve(base.P1)} e ${giornoBreve(base.P2)} ore 14:00`, DATE.E.testo],
  ];
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <h2 id={ID_TITOLO_LEGENDA} tabIndex={-1} className="text-xl font-extrabold text-ink outline-none">
          Legenda
        </h2>
        <button
          type="button"
          onClick={onChiudi}
          className="inline-flex h-9 items-center gap-1.5 rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 text-sm font-semibold text-ink hover:bg-ritiri-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          <X className="h-4 w-4" aria-hidden="true" />
          Chiudi
        </button>
      </div>

      <Sezione titolo="Le date">
        <p className="mb-2 text-xs text-ritiri-th-testo">Calendario base di adesso, lo stesso della testata.</p>
        <dl className="space-y-2 text-sm" data-legenda="date">
          {date.map(([sigla, quando, testo]) => (
            <div key={sigla} className="grid gap-x-3 sm:grid-cols-[13.5rem_minmax(0,1fr)]">
              <dt className="font-plex font-semibold text-ink">
                {sigla}: {quando}
              </dt>
              <dd className="text-ink">{testo}</dd>
            </div>
          ))}
        </dl>
      </Sezione>

      <Sezione titolo="Le barre">
        <ul className="space-y-1.5 text-sm text-ink">
          <li>{BARRE.adesso}</li>
          <li>{BARRE.aP2}</li>
          <li>{BARRE.percentuale}</li>
        </ul>
        <ul className="mt-3 space-y-2 text-sm text-ink">
          {BARRE.colori.map((c) => (
            <li key={c.tono} className="grid grid-cols-[5rem_minmax(0,1fr)] items-center gap-3">
              <BarraRitiri valore={ESEMPIO_BARRA[c.tono]} massimo={1} tono={c.tono} />
              <span>{c.testo}</span>
            </li>
          ))}
          <li className="grid grid-cols-[5rem_minmax(0,1fr)] items-center gap-3">
            <span className="text-right font-plex font-semibold">—</span>
            <span>{BARRE.mancante}</span>
          </li>
        </ul>
      </Sezione>

      <Sezione titolo="Gli esiti">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] border-collapse text-sm" data-legenda="esiti">
            <thead>
              <tr>
                {["Esito", "Cosa significa", "Cosa fare"].map((t) => (
                  <th key={t} scope="col" className="bg-ritiri-th-fondo px-2.5 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-ritiri-th-testo">
                    {t}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ESITI.map((e) => (
                <tr key={e.esito} className="border-t border-ritiri-bordo align-top">
                  <th scope="row" className="px-2.5 py-2 text-left font-normal">
                    <BadgeEsito esito={e.esito} testo={e.nome} />
                  </th>
                  <td className="px-2.5 py-2 text-ink">{e.quando}</td>
                  <td className="px-2.5 py-2 text-ink">{e.cosaFare}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Sezione>

      <Sezione titolo="Le etichette">
        <p className="mb-2 text-xs text-ritiri-th-testo">I chip grigi sotto le barre. Nell'elenco, toccando un chip si legge il testo completo di quella cassaforte.</p>
        <dl className="space-y-2 text-sm" data-legenda="etichette">
          {Object.entries(ETICHETTE).map(([codice, e]) => (
            <div key={codice} className="grid gap-x-3 sm:grid-cols-[12rem_minmax(0,1fr)]">
              <dt>
                <span className="rounded-full border border-ritiri-bordo-forte bg-ritiri-th-fondo px-2 py-0.5 text-[11px] font-semibold text-ritiri-th-testo">
                  {e.titolo}
                </span>
              </dt>
              <dd className="text-ink">{e.testo}</dd>
            </div>
          ))}
        </dl>
      </Sezione>

      <Sezione titolo="Gli scenari">
        <dl className="space-y-2 text-sm">
          {SCENARI.map((s) => (
            <div key={s.titolo}>
              <dt className="font-semibold text-ink">{s.titolo}</dt>
              <dd className="text-ink">{s.testo}</dd>
            </div>
          ))}
        </dl>
      </Sezione>
    </div>
  );
}
